import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import countries from "i18n-iso-countries/index.js";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { flagEmoji } from "@/lib/flag-emoji";
import { CaretDown, Check, MagnifyingGlass, Globe, X } from "@phosphor-icons/react";

interface CountryComboboxProps {
  value: string | null;
  onChange: (code: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  excludeCodes?: string[];
  allowClear?: boolean;
  disabled?: boolean;
  className?: string;
}

export function CountryCombobox({
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  excludeCodes,
  allowClear = false,
  disabled = false,
  className,
}: CountryComboboxProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "bs" ? "bs" : "en";

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const countryNames = useMemo(
    () => countries.getNames(locale, { select: "official" }) as Record<string, string>,
    [locale]
  );

  const excluded = useMemo(
    () => new Set((excludeCodes ?? []).map((c) => c.toUpperCase())),
    [excludeCodes]
  );

  const options = useMemo(() => {
    const list = Object.entries(countryNames)
      .filter(([code]) => !excluded.has(code) || code === value)
      .map(([code, name]) => ({ code, name }));
    list.sort((a, b) => a.name.localeCompare(b.name, locale));
    return list;
  }, [countryNames, excluded, value, locale]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q)
    );
  }, [options, query]);

  const selectedName = value ? countryNames[value] ?? value : null;
  const effectiveSearchPlaceholder = searchPlaceholder ?? t("country.searchPlaceholder");

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "inline-flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm transition-colors",
            "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            disabled && "cursor-not-allowed opacity-50",
            className
          )}
        >
          {value ? (
            <>
              <span className="text-base leading-none">{flagEmoji(value)}</span>
              <span className="flex-1 truncate text-left text-foreground">{selectedName}</span>
              <span className="text-xs text-muted-foreground">{value}</span>
            </>
          ) : (
            <>
              <Globe className="size-4 text-muted-foreground" />
              <span className="flex-1 truncate text-left text-muted-foreground">
                {placeholder ?? "—"}
              </span>
            </>
          )}
          <CaretDown className="size-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <div className="border-b border-border/40 p-2">
          <div className="relative">
            <MagnifyingGlass className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={effectiveSearchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 pl-8 text-xs"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {allowClear && value !== null && (
            <>
              <button
                type="button"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                  setQuery("");
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
              >
                <X className="size-3.5" />
                <span className="flex-1">{t("country.other")}</span>
              </button>
              <div className="my-1 border-t border-border/30" />
            </>
          )}
          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">—</p>
          ) : (
            filtered.map((o) => {
              const active = o.code === value;
              return (
                <button
                  key={o.code}
                  type="button"
                  onClick={() => {
                    onChange(o.code);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
                    active
                      ? "bg-primary/[0.06] text-foreground"
                      : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                  )}
                >
                  <span className="text-sm leading-none">{flagEmoji(o.code)}</span>
                  <span className="flex-1 truncate font-medium">{o.name}</span>
                  <span className="text-[10px] text-muted-foreground">{o.code}</span>
                  {active && <Check className="size-3.5 shrink-0 text-primary" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
